import { useEffect, useState, useMemo } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { ArrowUpRight, Plus, X, Trophy, Download } from "lucide-react";
import api from "../lib/api";
import { Navbar } from "../components/Navbar";
import { Footer } from "../components/Footer";
import { useReveal } from "../hooks/useReveal";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import { Button } from "../components/ui/button";

const SLOTS = 3;

export default function ComparePage() {
  const rootRef = useReveal();
  const [models, setModels] = useState([]);
  const [searchParams, setSearchParams] = useSearchParams();
  const [selected, setSelected] = useState(() => {
    const ids = (searchParams.get("ids") || "").split(",").filter(Boolean);
    return Array.from({ length: SLOTS }, (_, i) => ids[i] || "");
  });

  useEffect(() => {
    api.get("/models").then(({ data }) => setModels(data));
  }, []);

  // Persist to URL
  useEffect(() => {
    const ids = selected.filter(Boolean).join(",");
    if (ids) searchParams.set("ids", ids);
    else searchParams.delete("ids");
    setSearchParams(searchParams, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selected]);

  const picks = selected.map((id) => models.find((m) => m.id === id) || null);
  const filled = picks.filter(Boolean);

  // Union of all spec keys, ordered by first appearance
  const allKeys = useMemo(() => {
    const seen = [];
    filled.forEach((m) => {
      Object.keys(m.specs).forEach((k) => {
        if (!seen.includes(k)) seen.push(k);
      });
    });
    return seen;
  }, [filled]);

  // Best-of-class highlight (numeric only)
  const bestForKey = (key) => {
    const nums = filled
      .map((m) => ({ id: m.id, v: parseFloat(String(m.specs[key] || "").replace(/[^0-9.]/g, "")) }))
      .filter((x) => !Number.isNaN(x.v));
    if (nums.length < 2) return null;
    return nums.reduce((a, b) => (b.v > a.v ? b : a)).id;
  };

  const setSlot = (i, id) => {
    const next = [...selected];
    next[i] = id === "__clear__" ? "" : id;
    setSelected(next);
  };

  return (
    <div ref={rootRef} className="bg-white" data-testid="compare-page">
      <Navbar />

      <section className="pt-36 md:pt-44 pb-12 bg-[#f5f5f7]">
        <div className="max-w-7xl mx-auto px-6 md:px-10 reveal">
          <div className="overline">Compare Bobcat Models</div>
          <h1 className="h1-hero text-5xl md:text-7xl mt-4">
            Side by side.
            <br />
            <span className="text-[#ff6a13]">Spec by spec.</span>
          </h1>
          <p className="mt-5 text-lg text-[#6b6b72] max-w-2xl">
            Pick up to 3 machines. We'll line up every spec — and highlight the leader.
          </p>
        </div>
      </section>

      {/* Slot pickers */}
      <section className="py-10 bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <div className="grid md:grid-cols-3 gap-4">
            {selected.map((id, i) => {
              const m = models.find((x) => x.id === id);
              return (
                <div
                  key={i}
                  className="rounded-3xl border border-black/5 bg-[#f5f5f7] overflow-hidden"
                  data-testid={`compare-slot-${i}`}
                >
                  {m ? (
                    <>
                      <div className="aspect-[4/3] bg-gradient-to-br from-[#f5f5f7] to-[#ecedf0] flex items-center justify-center">
                        <img src={m.image} alt={m.name} className="w-[78%] h-auto no-drag" />
                      </div>
                      <div className="p-5">
                        <div className="text-[10px] tracking-[0.2em] uppercase text-[#ff6a13] font-bold">{m.category}</div>
                        <div className="font-display text-lg font-bold mt-1">{m.name}</div>
                        <div className="mt-4 flex items-center justify-between gap-2">
                          <Select value={id} onValueChange={(v) => setSlot(i, v)}>
                            <SelectTrigger className="h-9 text-xs rounded-full border-black/10" data-testid={`slot-${i}-select`}>
                              <SelectValue placeholder="Change model" />
                            </SelectTrigger>
                            <SelectContent>
                              {models.map((mm) => (
                                <SelectItem key={mm.id} value={mm.id}>{mm.name}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          <button
                            onClick={() => setSlot(i, "__clear__")}
                            className="text-xs text-[#86868b] hover:text-[#ff6a13] inline-flex items-center gap-1"
                            data-testid={`slot-${i}-clear`}
                          >
                            <X size={12} /> Clear
                          </button>
                        </div>
                      </div>
                    </>
                  ) : (
                    <div className="aspect-[4/3] flex flex-col items-center justify-center p-6 text-center">
                      <div className="w-12 h-12 rounded-full bg-white border border-black/10 flex items-center justify-center mb-3">
                        <Plus size={20} className="text-[#ff6a13]" />
                      </div>
                      <div className="font-bold mb-2">Add a model</div>
                      <Select onValueChange={(v) => setSlot(i, v)}>
                        <SelectTrigger className="h-10 max-w-xs rounded-full border-black/10 text-sm" data-testid={`slot-${i}-add`}>
                          <SelectValue placeholder="Pick a Bobcat" />
                        </SelectTrigger>
                        <SelectContent>
                          {models.map((mm) => (
                            <SelectItem key={mm.id} value={mm.id}>{mm.name}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Spec table */}
      <section className="pb-24 bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          {filled.length === 0 ? (
            <div className="py-16 text-center text-[#86868b]" data-testid="compare-empty">
              Pick a model above to start comparing.
            </div>
          ) : (
            <div className="overflow-x-auto rounded-3xl border border-black/5">
              <table className="w-full text-sm">
                <thead className="bg-[#f5f5f7]">
                  <tr>
                    <th className="text-left p-4 font-semibold text-[#86868b] uppercase tracking-widest text-xs">Spec</th>
                    {selected.map((id, i) => {
                      const m = models.find((x) => x.id === id);
                      return (
                        <th key={i} className="text-left p-4 font-semibold uppercase tracking-widest text-xs">
                          {m ? m.code : "—"}
                        </th>
                      );
                    })}
                  </tr>
                </thead>
                <tbody>
                  {allKeys.map((k) => {
                    const winner = bestForKey(k);
                    return (
                      <tr key={k} className="border-t border-black/5">
                        <td className="p-4 font-semibold text-[#111113]">{k}</td>
                        {selected.map((id, i) => {
                          const m = models.find((x) => x.id === id);
                          const v = m?.specs[k];
                          const isWinner = winner && winner === id;
                          return (
                            <td
                              key={i}
                              className={`p-4 ${isWinner ? "bg-[#ff6a13]/10 text-[#ff6a13] font-bold" : "text-[#333]"}`}
                              data-testid={`cell-${i}-${k.toLowerCase().replace(/\s+/g, "-")}`}
                            >
                              {v ? (
                                <span className="inline-flex items-center gap-1.5">
                                  {v}
                                  {isWinner && <Trophy size={13} />}
                                </span>
                              ) : (
                                <span className="text-[#bbb]">—</span>
                              )}
                            </td>
                          );
                        })}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}

          {filled.length > 0 && (
            <div className="mt-8 flex flex-wrap gap-3">
              {filled.map((m) => (
                <a
                  key={m.id}
                  href={`${process.env.REACT_APP_BACKEND_URL}/api/models/${m.id}/spec-sheet.pdf`}
                  target="_blank"
                  rel="noreferrer"
                  className="btn-ghost"
                  data-testid={`download-${m.id}`}
                >
                  <Download size={16} /> {m.code} Spec Sheet (PDF)
                </a>
              ))}
              <Link to="/book-demo" className="btn-primary ml-auto">
                Book a Demo <ArrowUpRight size={16} />
              </Link>
            </div>
          )}
        </div>
      </section>

      <Footer />
    </div>
  );
}
